import { Box, Button, Flex, Image, Input, Text } from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { RichTextField } from '../../../../components/RichTextField'
import { FormSelect } from '../../../../components/Select'
import { IProfessionalFields } from '../../../../services/Event/EventService'
import { SelectTeacher } from '../../../../services/Teacher/types'
import { IEventForm } from './eventFormShared'

interface IProps {
  form: IEventForm
  setForm: Dispatch<SetStateAction<IEventForm>>
  fieldErrors: Record<string, string[]>
  saving: boolean
  teachers: SelectTeacher[]
  addExternalProfessional: () => void
  addTeacherProfessional: (teacherId: number) => void
  updateProfessional: (id: string, field: keyof IProfessionalFields, value: string) => void
  removeProfessional: (id: string) => void
}

export function ProfessionalsTab({
  form,
  setForm,
  fieldErrors,
  saving,
  teachers,
  addExternalProfessional,
  addTeacherProfessional,
  updateProfessional,
  removeProfessional
}: IProps) {
  return (
    <Flex direction="column" gap={5}>
      <Flex gap={3} alignItems="center">
        <Button size="sm" variant="outline" colorScheme="gray" onClick={addExternalProfessional} disabled={saving}>
          + Adicionar Profissional Externo
        </Button>
        <Box>
          <FormSelect
            size="sm"
            placeholder="Selecionar Professor"
            // Controlled to always show the placeholder: the picked teacher is added to
            // `form.professionals` (not held here) and immediately filtered out of the
            // options below, so an uncontrolled select could keep showing a stale value.
            value=""
            onChange={(value) => addTeacherProfessional(Number(value))}
            options={teachers
              .filter((teacher) => !form.professionals.some((p) => p.teacher_id === teacher.id))
              .map((teacher) => ({ value: teacher.id, label: teacher.name }))}
            isDisabled={saving}
            w="200px"
          />
        </Box>
      </Flex>

      {form.professionals.map((prof, profIdx) => {
        const isTeacher = !!prof.teacher_id
        // Lido uma vez: o `find(...)!.banner_picture!` que havia aqui estourava se o professor
        // saísse da lista entre o `find` do teste e o `find` do `src`.
        const teacherBanner = teachers.find((teacher) => teacher.id === prof.teacher_id)?.banner_picture
        return (
          <Box key={prof._id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Flex justifyContent="space-between" alignItems="center" mb={3}>
              <Text fontWeight="bold" fontSize="sm">
                {isTeacher ? '👨‍🏫 Professor' : '👤 Profissional Externo'}
              </Text>
              <Button
                size="xs"
                colorScheme="red"
                variant="ghost"
                onClick={() => removeProfessional(prof._id)}
                disabled={saving}
              >
                Remover
              </Button>
            </Flex>

            <Flex direction="column" gap={3}>
              <Flex gap={3}>
                <FormField
                  label="Título"
                  error={fieldErrors[`professionals.${profIdx}.title`]?.[0]}
                  isRequired
                  flex="1"
                >
                  <Input
                    size="sm"
                    value={prof.title}
                    onChange={(e) => updateProfessional(prof._id, 'title', e.target.value)}
                    disabled={saving || isTeacher}
                    bg={isTeacher ? 'gray.50' : undefined}
                  />
                </FormField>
                <FormField label="Nome" error={fieldErrors[`professionals.${profIdx}.name`]?.[0]} isRequired flex="1">
                  <Input
                    size="sm"
                    value={prof.name}
                    onChange={(e) => updateProfessional(prof._id, 'name', e.target.value)}
                    disabled={saving || isTeacher}
                    bg={isTeacher ? 'gray.50' : undefined}
                  />
                </FormField>
              </Flex>

              {!isTeacher && (
                <Box>
                  <ImageCropInput
                    label="Foto"
                    value={prof.thumb ?? prof.thumbPreview ?? null}
                    onChange={(file) =>
                      setForm((prev) => ({
                        ...prev,
                        professionals: prev.professionals.map((p) =>
                          p._id === prof._id ? { ...p, thumb: file, thumbPreview: null } : p
                        )
                      }))
                    }
                    maxSizeMB={5}
                    previewHeight="150px"
                    isDisabled={saving}
                  />
                </Box>
              )}

              {isTeacher && teacherBanner && (
                <Box>
                  <FormField label="Foto">
                    <Image src={teacherBanner} alt={prof.name} boxSize="300px" objectFit="contain" borderRadius="md" />
                  </FormField>
                </Box>
              )}

              <RichTextField
                label="Mini Bio"
                value={prof.bio}
                onChange={(bio) => updateProfessional(prof._id, 'bio', bio)}
                placeholder="Mini bio do profissional..."
                isReadOnly={isTeacher}
              />
            </Flex>
          </Box>
        )
      })}
    </Flex>
  )
}
